"use client";

import { japanPracticeDay } from "@tamariba/drills";

import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import { useAuthoringScope } from "../authoring-scope";
import { DrillBreadcrumbs } from "./breadcrumbs";
import { DrillCollectionEditor } from "./collection-editor";
import { DrillLibrary } from "./library";
import { type PracticePlanChoice, PracticePlanEditor } from "./practice-plan";
import { DrillError, DrillShell, redirectForAuth } from "./shared";
import {
  type DrillCollection,
  type DrillCollectionDetail,
  request,
} from "./types";

export function DrillCollectionPage({
  id,
  layoutId,
}: {
  id: string;
  layoutId?: string;
}) {
  const authoring = useAuthoringScope();
  const router = useRouter();
  const params = useSearchParams();
  const [collection, setCollection] = useState<DrillCollectionDetail>();
  const [editing, setEditing] = useState(
    id === "new" || params.get("edit") === "1",
  );
  const [error, setError] = useState("");
  const [planChoice, setPlanChoice] = useState<PracticePlanChoice>();
  const [editingPlan, setEditingPlan] = useState(false);
  const load = useCallback(async () => {
    if (id === "new") return;
    setError("");
    try {
      const result = await request<DrillCollectionDetail>(
        authoring.api(`/api/drill-collections/${id}`),
      );
      if (!authoring.admin && !result.isPlaylist) {
        setPlanChoice(
          await request<PracticePlanChoice>(
            `/api/drill-collections/${id}/practice-plan`,
          ),
        );
      }
      setCollection(result);
    } catch (error) {
      if (!redirectForAuth(error, authoring.href("/login")))
        setError((error as Error).message);
    }
  }, [id, authoring]);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    if (!collection?.practiceDay || id === "new") return;
    let timer: ReturnType<typeof setTimeout>;
    const refresh = () => {
      if (
        document.visibilityState === "visible" &&
        collection.practiceDay !== japanPracticeDay()
      )
        void load();
      schedule();
    };
    const schedule = () => {
      clearTimeout(timer);
      const today = japanPracticeDay();
      const nextMidnight =
        new Date(`${today}T00:00:00+09:00`).getTime() + 86400000;
      timer = setTimeout(
        refresh,
        collection.practiceDay !== today
          ? 60000
          : Math.max(100, nextMidnight - Date.now() + 100),
      );
    };
    refresh();
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [collection?.practiceDay, id, load]);
  function saved(saved: DrillCollection) {
    setCollection({
      ...saved,
      practiceDay: collection?.practiceDay ?? japanPracticeDay(),
      drills: collection?.drills ?? [],
    });
    setEditing(false);
    if (id !== "new") void load();
    if (id !== "new" && params.get("edit") === "1")
      router.replace(authoring.href(`/drills/collections/${id}`), {
        scroll: false,
      });
    if (id === "new") {
      router.replace(
        layoutId
          ? authoring.href(
              `/drills/new?collection=${saved.id}&layout=${encodeURIComponent(layoutId)}`,
            )
          : authoring.href(`/drills/collections/${saved.id}`),
      );
    }
    window.scrollTo({ top: 0 });
  }
  if ((editing && collection?.canEdit) || id === "new")
    return (
      <DrillShell>
        <DrillCollectionEditor
          key={collection?.id ?? "new"}
          collection={collection}
          onSaved={saved}
          onCancel={() => {
            if (id === "new")
              router.push(
                layoutId
                  ? authoring.href(
                      `/drills/new?layout=${encodeURIComponent(layoutId)}`,
                    )
                  : authoring.href("/drills"),
              );
            else {
              setEditing(false);
              if (params.get("edit") === "1")
                router.replace(authoring.href(`/drills/collections/${id}`), {
                  scroll: false,
                });
            }
          }}
        />
      </DrillShell>
    );
  if (collection && editingPlan && planChoice)
    return (
      <DrillShell>
        <DrillBreadcrumbs collection={collection} current="練習方法" />
        <PracticePlanEditor
          collectionId={collection.id}
          choice={planChoice}
          onCancel={() => setEditingPlan(false)}
          onSaved={() => {
            setEditingPlan(false);
            void load();
            window.scrollTo({ top: 0 });
          }}
        />
      </DrillShell>
    );
  if (collection)
    return (
      <DrillLibrary
        collection={collection}
        onEdit={() => setEditing(true)}
        onOrdered={setCollection}
        planChoice={planChoice}
        onEditPlan={() => setEditingPlan(true)}
      />
    );
  return (
    <DrillShell>
      <DrillBreadcrumbs current="ドリル集の詳細" />
      {error ? (
        <>
          <DrillError message={error} />
          <button
            type="button"
            className="secondary-button"
            onClick={() => void load()}
          >
            再読み込み
          </button>
        </>
      ) : (
        <p className="drill-loading" role="status">
          ドリル集を読み込み中…
        </p>
      )}
    </DrillShell>
  );
}
